// ─────────────────────────────────────────────────────────────────────────────
// MerchSpotlight, the Merch Manager hero. 1:1 port of the merch spotlight in
// dashboard-wiring.js (paintMerchSpotlight) + #merch-spotlight-card, on CD's
// spotlight shell. "What's selling": 30-day total badge + Today / 30-day / Top
// seller stats. The best-sellers list lives below in <MerchBestsellers>. Fed from
// the dashboard merch summary (Shopify/Square), no extra fetch.
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, JSX } from 'react';
import type { MerchSummary } from '../../lib';

function spotlightStyle(photoUrl: string): CSSProperties | undefined {
  if (!photoUrl) return undefined;
  const safe = photoUrl.replace(/["'()\\\s]/g, '');
  return {
    backgroundImage:
      'linear-gradient(135deg,rgba(26,14,13,0.92) 0%,rgba(45,21,24,0.78) 45%,rgba(139,26,43,0.55) 100%),' +
      "url('" + safe + "')",
    backgroundSize: 'cover, cover',
    backgroundPosition: 'center, center',
    backgroundRepeat: 'no-repeat, no-repeat',
  };
}

export function merchMoney(n: number, currency: string): string {
  const cur = currency === 'USD' ? '$' : currency ? currency + ' ' : '$';
  return cur + Math.round(Number(n || 0)).toLocaleString('en-US');
}

export interface MerchSpotlightProps {
  readonly merch: MerchSummary | null;
  readonly photoUrl?: string;
}

export function MerchSpotlight({ merch, photoUrl = '' }: MerchSpotlightProps): JSX.Element {
  const hasPhoto = !!photoUrl;
  const cardClass = 'spotlight' + (hasPhoto ? ' spotlight--has-photo' : '');
  const connected = !!merch && merch.source !== 'none';
  const cur = merch?.currency || 'USD';
  const top = merch?.bestsellers ?? [];
  const sub = !connected
    ? 'Connect Shopify or Square in Settings → Connectors to wire sales.'
    : merch!.posOnly
      ? 'POS sales, venue-table only. Reorder the table for tonight based on what’s hot.'
      : 'All Shopify sales · last 30 days. Reorder the table for tonight based on what’s hot.';

  return (
    <article className={cardClass} id="merch-spotlight-card" style={spotlightStyle(photoUrl)}>
      {!hasPhoto && <div className="spotlight__bg" aria-hidden="true"></div>}

      <header className="spotlight__head">
        <div>
          <p className="spotlight__eyebrow">★ Top movers · last 30 days</p>
          <h2 className="spotlight__title disp">What&apos;s selling</h2>
          <p className="spotlight__sub">{sub}</p>
        </div>
        {connected && <span className="delta delta--up">{merchMoney(merch!.sales30d, cur)}</span>}
      </header>

      <div className="spotlight__stats">
        <div className="statile">
          <p className="statile__label">Today</p>
          <p className="statile__value">{connected ? merchMoney(merch!.salesToday, cur) : '-'}</p>
        </div>
        <div className="statile">
          <p className="statile__label">30-day</p>
          <p className="statile__value">{connected ? merchMoney(merch!.sales30d, cur) : '-'}</p>
        </div>
        <div className="statile">
          <p className="statile__label">Top seller</p>
          <p className="statile__value statile__value--sm">{top.length ? top[0].title : '-'}</p>
        </div>
      </div>
    </article>
  );
}
